import { ErpDiv } from './ErpHtml';
import React from 'react';
import { Card } from '@/components/ui/card';
import ErpCardHeader from './ErpCardHeader';
import ErpCardBody from './ErpCardBody';
import ErpCardFooter from './ErpCardFooter';
import ErpCardTitle from './ErpCardTitle';

export interface ErpCardProps {
  title?: string;
  subTitle?: string;
  header?: React.ReactNode;
  headerSlot?: React.ReactNode;
  footer?: string;
  footerSlot?: React.ReactNode;
  bodyClass?: string;
  hoverable?: boolean;
  noBody?: boolean;
  align?: 'left' | 'center' | 'right';
  bgVariant?: string;
  textVariant?: string;
  headerBgVariant?: string;
  className?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
}

// Literal Tailwind classes — `bg-${bgVariant}` templates built class names
// the JIT compiler never sees, so unlisted variants silently rendered nothing.
const BG_CLASS_MAP: Record<string, string> = {
  default: '',
  white: 'bg-white',
  transparent: 'bg-transparent',
  primary: 'bg-primary text-primary-foreground',
  secondary: 'bg-secondary text-secondary-foreground',
  success: 'bg-green-500 text-white',
  warning: 'bg-amber-500 text-white',
  danger: 'bg-red-500 text-white',
  info: 'bg-blue-500 text-white',
  light: 'bg-muted',
  muted: 'bg-muted',
};

const TEXT_CLASS_MAP: Record<string, string> = {
  white: 'text-white',
  default: '',
  muted: 'text-muted-foreground',
  light: 'text-muted-foreground',
  primary: 'text-primary',
  success: 'text-green-600',
  warning: 'text-amber-600',
  danger: 'text-red-600',
};

const ErpCard: React.FC<ErpCardProps> = ({
  title,
  subTitle,
  header,
  headerSlot,
  footer,
  footerSlot,
  bodyClass,
  noBody,
  align,
  bgVariant,
  textVariant,
  headerBgVariant,
  className = '',
  style,
  children,
}) => {
  const alignClass = align
    ? `text-${align === 'left' ? 'start' : align === 'right' ? 'end' : 'center'}`
    : '';

  const variantClass = bgVariant ? (BG_CLASS_MAP[bgVariant] ?? '') : '';
  const textVariantClass = textVariant ? (TEXT_CLASS_MAP[textVariant] ?? '') : '';
  const headerBgVariantClass = headerBgVariant ? (BG_CLASS_MAP[headerBgVariant] ?? '') : '';

  const cardClasses = [
    alignClass,
    variantClass,
    textVariantClass,
    noBody ? 'p-0 shadow-none border-none' : '',
    className,
  ].filter(Boolean).join(' ');

  return (
    <Card className={cardClasses} style={style}>
      {(header || headerSlot) && (
        <ErpCardHeader className={headerBgVariantClass}>
          {header}
          {headerSlot}
        </ErpCardHeader>
      )}
      {!noBody ? (
        <ErpCardBody className={bodyClass}>
          {title && <ErpCardTitle>{title}</ErpCardTitle>}
          {subTitle && <ErpDiv className="text-sm text-muted-foreground">{subTitle}</ErpDiv>}
          {children}
        </ErpCardBody>
      ) : (
        children
      )}
      {(footer || footerSlot) && (
        <ErpCardFooter>
          {footer}
          {footerSlot}
        </ErpCardFooter>
      )}
    </Card>
  );
};

export default React.memo(ErpCard);
